import {
  Button,
  FormLabel,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  useToast,
  Icon,
  Link as ChakraLink,
  Text,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { PiUser } from 'react-icons/pi';
import { PasswordInput } from 'src/components/PasswordInput';
import { WithFormControl } from 'src/components/WithFormControl';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginFormSchema } from '../../schemas/loginFormSchema';
import { LoginFormSchemaData } from '../../schemas/types';
import { AuthFormBaseContainer } from '@/components/AuthFormBaseContainer/AuthFormBaseContainer';
import { getRememberedUser, handleRememberUser } from '../../utils/rememberUser';
import { useNavigate } from 'react-router';
import { useAuthMutation } from '@/services/queries/auth';
import { LoginPayloadData } from '@/model/auth';
import { useCallback } from 'react';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { useTranslation } from 'react-i18next';

export const LoginFormContainer = () => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<LoginFormSchemaData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: getRememberedUser() ?? '',
    },
  });

  const toast = useToast();
  const navigate = useNavigate();
  const authMutation = useAuthMutation();

  const onSubmit: SubmitHandler<LoginFormSchemaData> = useCallback(
    async data => {
      const payload: LoginPayloadData = {
        password: data.password,
        email: data.email,
      };
      authMutation.mutate(payload, {
        onSuccess: () => {
          handleRememberUser(data);
          navigate('/login/2fa');
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: resp?.message ?? t('pages.login.components.login_form_container.login_form_error_title'),
            description: t('pages.login.components.login_form_container.login_form_error_description'),
          });
        },
      });
    },
    [authMutation, navigate, toast],
  );

  return (
    <AuthFormBaseContainer
      title={t('pages.login.components.login_form_container.login_form_title')}
      description={t('pages.login.components.login_form_container.login_form_description')}
    >
      <Flex
        onSubmit={handleSubmit(onSubmit)}
        as='form'
        direction='column'
        align='stretch'
        gap='small'
        px={5}
        w='full'
        maxW='400px'
        alignSelf='center'
      >
        <WithFormControl variant='floating' errorMessage={errors.email?.message} isInvalid={!!errors.email}>
          <InputGroup>
            <FormLabel htmlFor='email'> {t('pages.login.components.login_form_container.login_form_email_label')}</FormLabel>
            <Input
              id='email'
              variant='flushed'
              {...register('email')}
              placeholder={t('pages.login.components.login_form_container.login_form_email_placeholder')}
            />

            <InputRightElement>
              <Icon as={PiUser} width={4} height={4} />
            </InputRightElement>
          </InputGroup>
        </WithFormControl>

        <WithFormControl variant='floating' errorMessage={errors.password?.message} isInvalid={!!errors.password}>
          <PasswordInput
            variant='flushed'
            placeholder='******'
            label={<FormLabel> {t('pages.login.components.login_form_container.login_form_password_label')}</FormLabel>}
            {...register('password')}
          />
        </WithFormControl>

        <Text mt='-18px' fontSize='16px'>
          {t('pages.login.components.login_form_container.login_form_required_fields')}
        </Text>

        <Flex alignItems='center' justifyContent='space-between' gap='8px'>
          <ChakraLink as={Link} to='/login/esqueci-minha-senha' color='brand.500' fontSize='16px' textDecoration='underline'>
            {t('pages.login.components.login_form_container.login_form_forgot_password')}
          </ChakraLink>
          <Button type='submit' w='150px' alignSelf='end' variant='gradient'>
            {t('pages.login.components.login_form_container.login_form_button_enter')}
          </Button>
        </Flex>
      </Flex>
      <CenteredSpinner isLoading={authMutation.isLoading} />
    </AuthFormBaseContainer>
  );
};
